// Calendar event detail modal open/close helpers — registered via
// context.functions so any element can dispatch them with:
//   el.call('openEventDetailModal', event)   // calendar event object
//   el.call('closeEventDetailModal')
//
// Migrated 2026-06-01 from `root.update({ selectedEvent: ev,
// showEventDetail: true })` to a @symbo.ls/windows custom-template
// layer. `root.selectedEvent` retained as data carrier — the body's
// reactive reads + the chat/calendar hash sync still pull from it.

import { suppressInitialModalFocus } from './modalLayerLifecycle.js'

const EVENT_DETAIL_LAYER_ID = 'cal-event-detail'
const ACTIVE_EVENT_DETAIL_KEY = Symbol.for('symbols.calendar.activeEventDetail')

const _rootState = (el) =>
  el?.state?.root || el?.getRoot?.()?.state?.root || el?.getRoot?.()?.state || null

// URL deep-link marker — same shape as the tickets modal's `?ticket=<id>`
// (2026-06-08 per Nika "event modal url same as tickets"). A query param
// instead of the calendar's `#event=` hash so the link is route-independent
// — it survives opening the calendar inside the home AppWindow (where the
// hash sync is gated off) and is shareable like a ticket link.
const _eventSnapshot = (event) => ({
  id: event.id,
  title: event.title,
  date: event.date,
  startTime: event.startTime,
  endTime: event.endTime,
  startAt: event.startAt,
  endAt: event.endAt,
  creatorEmail: event.creatorEmail,
  organizerEmail: event.organizerEmail,
  description: event.description,
  invitees: event.invitees,
  integrationMetadata: event.integrationMetadata,
  googleEventId: event.googleEventId,
  googleCalendarId: event.googleCalendarId,
  color: event.color,
  type: event.type
})

const _setEventUrlParam = (event) => {
  if (typeof window === 'undefined' || event?.id == null) return
  try {
    const url = new URL(window.location.href)
    url.searchParams.set('event', String(event.id))
    // The calendar feed and the PageSlot restore asynchronously. Carry the
    // already-open event with its deep link so a refresh can render details
    // immediately instead of mounting the modal against selectedEvent=null.
    url.searchParams.set('eventSnapshot', JSON.stringify(_eventSnapshot(event)))
    window.history.replaceState(null, '', url.toString())
  } catch (_) {}
}
// Only while the address still names THIS event. A close that a navigation
// causes (`closeStaleEventDetailModal`) runs on the NEW address, and that
// address can already name ANOTHER event (Back / Forward to its link): its
// params must stay for that event's own restore.
const _clearEventUrlParam = (eventId) => {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    const named = url.searchParams.get('event')
    if (named !== null && (eventId == null || named === String(eventId))) {
      for (const key of [
        'event',
        'eventSeries',
        'eventFingerprint',
        'eventSnapshot',
        'eventRsvp'
      ]) {
        url.searchParams.delete(key)
      }
      window.history.replaceState(null, '', url.toString())
    }
  } catch (_) {}
}

export function openEventDetailModal(event) {
  if (!event) return
  const root = _rootState(this)
  if (!root) return

  // Deep-link restoration can be attempted by more than one keep-alive
  // Calendar instance while the workspace PageSlot is settling. Opening the
  // same event again would push another windows layer; after a refresh those
  // duplicate layers surfaced as an empty modal and console warnings for
  // ModalLayer_0, ModalLayer_1, ... . The first opener owns the layer.
  if (
    root.showEventDetail &&
    String(root.selectedEvent?.id || '') === String(event.id || '')
  ) {
    return
  }

  // A PageSlot calendar and its keep-alive counterpart can restore the same
  // query-param event through different root objects. Root-local carrier
  // flags cannot dedupe those calls, but both instances share one document
  // and one windows layer host. Claim the event id before pushing the layer
  // so even two synchronous restore calls produce one dialog.
  const eventId = event.id == null ? null : String(event.id)
  const eventDocument = typeof document === 'object' && document ? document : null
  if (eventDocument && eventId != null) {
    if (eventDocument[ACTIVE_EVENT_DETAIL_KEY] === eventId) return
    eventDocument[ACTIVE_EVENT_DETAIL_KEY] = eventId
  }

  // Normalize description + invitees so the skeleton state in
  // CalEventDetailModal doesn't get stuck. The body's reactive props
  // treat `undefined` as "still loading" and show indefinite
  // skeletons. Summary feeds (intranetEvents on the home dashboard,
  // certain mention links) deliver events without those fields at
  // all → modal renders, fires fetchEventDetails on init, but if the
  // fetch silently fails or returns no extra data the description /
  // invitees fields stay undefined and the skeleton never resolves.
  //
  // Pre-seeding with whatever the caller already has (preserving
  // values they passed in) and `null` / `[]` fallback means:
  //   - If the SDK fetch populates real values, both are overwritten
  //     in fetchEventDetails — caller never sees a stale empty state.
  //   - If the fetch never resolves, the body renders the loaded
  //     branch with empty content; the user sees the title + time +
  //     header info and can close the modal cleanly instead of
  //     staring at a never-ending skeleton.
  const normalized = {
    ...event,
    // Creator/organizer parity (2026-06-10) — the home "Coming up" feed
    // (intranetEvents) delivers the organizer as `organizerEmail`, while the
    // calendar `events` feed maps the SAME `organizer_email` DB column to
    // `creatorEmail` (loadPrivateData). CalEventDetailModal's "Created by"
    // row reads `creatorEmail` only, so without this fallback that row
    // silently vanished when the modal was opened from the home dashboard —
    // the visible "events modal looks worse on /" gap. Normalize once here,
    // the single choke-point both the home and calendar event cells share.
    creatorEmail: event.creatorEmail || event.organizerEmail || null,
    description: event.description ?? null,
    invitees: Array.isArray(event.invitees) ? event.invitees : []
  }
  root.update({ selectedEvent: normalized, showEventDetail: true })
  // Reflect the open event in the URL as `?event=<id>` (tickets-style,
  // route-independent) so the link is shareable and works from `/`.
  _setEventUrlParam(normalized)
  // onClose fires when the layer is popped via backdrop click or ESC
  // (the windows ModalLayer dispatches it from its dismiss path). Use
  // it to clear selectedEvent so reopen-on-different-event doesn't
  // surface the previous one's data for a frame, and so onUpdate's
  // hash-clear runs.
  suppressInitialModalFocus(EVENT_DETAIL_LAYER_ID)
  // MODAL-X-OVERFLOW-1: an explicit maxHeight keeps the outer ModalWindow
  // ceiling bounded so tall content / a short viewport can't push
  // ModalHeader's Close X off-screen (@symbo.ls/windows defaults maxHeight
  // to null otherwise). Kept as a raw openModal call (not the shared
  // openCustomModal wrapper) — this file's own unit test pins the literal
  // 'openModal' dispatch name.
  this.call('openModal', {
    id: EVENT_DETAIL_LAYER_ID,
    template: 'custom',
    width: 'I',
    maxWidth: '92vw',
    maxHeight: '90vh',
    content: { extends: 'CalEventDetailModal' },
    onClose: () => {
      if (eventDocument && eventDocument[ACTIVE_EVENT_DETAIL_KEY] === eventId) {
        delete eventDocument[ACTIVE_EVENT_DETAIL_KEY]
      }
      const selected = root.selectedEvent
      const responses = selected?.integrationMetadata?.calendarParticipantResponses
      let events = root.events
      if (responses && selected) {
        const seriesBase = String(selected.googleEventId || '').replace(/_\d{8}(?:T\d{6}Z)?$/, '')
        events = (root.events || []).map((event) => {
          const sameEvent = event.id === selected.id
          const sameSeries =
            seriesBase &&
            String(event.googleEventId || '').replace(/_\d{8}(?:T\d{6}Z)?$/, '') === seriesBase
          if (!sameEvent && !sameSeries) return event
          return {
            ...event,
            integrationMetadata: {
              ...(event.integrationMetadata || {}),
              calendarParticipantResponses: responses
            }
          }
        })
      }
      root.update({ events, selectedEvent: null, showEventDetail: false })
      _clearEventUrlParam(eventId)
    }
  })
}

export function closeEventDetailModal() {
  // selectedEvent + showEventDetail are cleared by the layer's onClose
  // callback (see openEventDetailModal above). All this needs to do is
  // pop the windows-plugin layer; the onClose path handles the rest.
  // The X button's onClick + any other explicit-close consumer routes
  // through here.
  this.call('closeModal', EVENT_DETAIL_LAYER_ID)
}

// el.call('closeStaleEventDetailModal', wantedId?) — close the event popover
// once the address no longer names its event. Returns true when it closed it.
//
// The popover is a GLOBAL windows layer, so a route change does not unmount
// it, and nothing else closes it on navigation: Back from
// `/calendar?event=A` to the AI thread left event A open over the thread.
// The shell runs this on popstate and on a pathname change (app.js
// `_onSpaNavigation`). The open event stays while `?event=` still names it,
// so an in-route replaceState that keeps `event=A` (the calendar's own
// `#date=` / `eventSnapshot` writes) never closes it.
//
// `wantedId`: the event id the caller's own reader resolved (null: the
// address names none). The calendar page passes it when an in-route link
// changes the event (pages/index.js `_followEventParam`): in a PageSlot pane
// its event lives in the pane's query, and an RSVP fingerprint link shows
// its row under that row's own id. Left out, the shell's reader decides.
export function closeStaleEventDetailModal(wantedId) {
  const root = _rootState(this)
  const openId = root?.showEventDetail ? root.selectedEvent?.id : null
  if (openId == null) return false
  const wanted = wantedId !== undefined ? wantedId : this.call('readPageParam', 'event')
  if (wanted != null && String(wanted) === String(openId)) return false
  closeEventDetailModal.call(this)
  return true
}
